/**
 * SubstrateCalculator - Main calculator orchestrator
 *
 * Coordinates between:
 * - AreaManager: manages area CRUD
 * - CalculationEngine: handles calculations
 * - ProductManager: manages products and pricing
 * - ResultsDisplay: manages results display
 * - TemplateManager: provides template access
 */

import TemplateManager from './TemplateManager.js';
import AreaManager from './AreaManager.js';
import CalculationEngine from './CalculationEngine.js';
import ProductManager from './ProductManager.js';
import ResultsDisplay from './ResultsDisplay.js';
import { t } from './translations.js';

// Import calculator classes
import BoxCalculator from './calculators/BoxCalculator.js';
import PotCalculator from './calculators/PotCalculator.js';
import GroundCalculator from './calculators/GroundCalculator.js';
import CustomCalculator from './calculators/CustomCalculator.js';

export default class SubstrateCalculator {
    constructor(rootElement) {
        this.root = rootElement;

        // Initialize managers
        this.templateManager = new TemplateManager();
        this.areaManager = new AreaManager(rootElement, this.templateManager);
        this.calculationEngine = new CalculationEngine();
        this.resultsDisplay = new ResultsDisplay(rootElement, this.templateManager, this.areaManager);

        // Product manager will be initialized after fetching products
        this.productManager = null;

        this.init();
    }

    /**
     * Initialize the calculator
     */
    async init() {
        this.bindEvents();
        this.areaManager.initializeAreas();
        await this.fetchCalculatorProducts();
    }

    /**
     * TEMPORARY: Show demo results for styling/testing
     */
    showDemoResults() {
        const demoData = {
            areas: [
                {
                    areaId: 1,
                    valid: true,
                    substrateVolume: 1020,
                    drainageVolume: 250,
                    dimensions: { x: 300, y: 200, z: 20 },
                    shapeType: 'box',
                    calculationMethod: 'measure'
                }
            ],
            totals: {
                substrateVolume: 1020,
                drainageVolume: 250
            }
        };

        this.calculateAndDisplay(demoData);
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
        // Add new area button
        const addAreaBtn = this.root.querySelector('[data-substrate-calculator="add-area"]');
        if (addAreaBtn) {
            addAreaBtn.addEventListener('click', () => this.areaManager.addArea());
        }

        // Calculate button
        const calculateBtn = this.root.querySelector('[data-substrate-calculator="calculate"]');
        if (calculateBtn) {
            calculateBtn.addEventListener('click', () => this.calculateAll());
        }

        // Delegate events for dynamic area buttons
        this.root.addEventListener('click', (e) => {
            const duplicateBtn = e.target.closest('[data-substrate-calculator="duplicate"]');
            const deleteBtn = e.target.closest('[data-substrate-calculator="delete"]');

            if (duplicateBtn) {
                this.areaManager.duplicateArea(duplicateBtn);
            }

            if (deleteBtn) {
                this.areaManager.deleteArea(deleteBtn);
            }
        });

        // Delegate events for shape type selection
        this.root.addEventListener('click', (e) => {
            const shapeTypeBtn = e.target.closest('[data-substrate-calculator="shape-type"]');
            if (shapeTypeBtn) {
                this.areaManager.selectShapeType(shapeTypeBtn);
            }
        });

        // Delegate events for calculation method change
        this.root.addEventListener('change', (e) => {
            if (e.target.matches('[data-substrate-calculator="calculation-method"]')) {
                this.areaManager.changeCalculationMethod(e.target);
            }
        });

        // Delegate events for drainage change
        this.root.addEventListener('change', (e) => {
            if (e.target.matches('[data-substrate-calculator="drainage"]')) {
                const area = e.target.closest('[data-substrate-calculator="area"]');
                if (area) {
                    const areaData = this.areaManager.getAreaData(area);
                    if (areaData) {
                        // Update the withDrainage value based on radio selection
                        areaData.withDrainage = e.target.value === 'with-drainage';
                        area.dataset.withDrainage = areaData.withDrainage ? 'true' : 'false';

                        // If results exist (even if hidden), recalculate automatically
                        const resultsContainer = this.root.querySelector('[data-substrate-calculator="results-container"]');
                        if (resultsContainer) {
                            this.calculateAll();
                        }
                    }
                }
            }
        });

        // Delegate events for known volume type change (liters vs area)
        this.root.addEventListener('change', (e) => {
            if (e.target.matches('[data-substrate-calculator="known-volume-type"]')) {
                this.areaManager.changeKnownVolumeType(e.target);
            }
        });
    }

    /**
     * Fetch calculator products from REST API
     */
    async fetchCalculatorProducts() {
        try {
            const productId = this.root.dataset.productId;

            // Use wpApiSettings.root if available, otherwise fallback to relative path
            const apiRoot = window.wpApiSettings?.root || '/wp-json/';
            const apiUrl = new URL(`${apiRoot}substrate-calculator/v1/products`, window.location.origin);

            if (productId) {
                apiUrl.searchParams.append('product_id', productId);
            }

            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const products = await response.json();

            // Initialize product manager with fetched products
            this.productManager = new ProductManager(
                this.root,
                this.templateManager,
                products
            );
        } catch (error) {
            console.error('Failed to fetch calculator products:', error);
            // Fallback to empty products or handle error
            this.productManager = new ProductManager(
                this.root,
                this.templateManager,
                this.getFallbackProducts()
            );
        }
    }

    /**
     * Calculate all areas and display results
     */
    calculateAll() {
        // Clear previous errors and error messages
        this.clearErrors();
        this.areaManager.areas.forEach(area => this.clearAreaErrors(area.element));

        const areas = this.areaManager.areas;

        // Calculate all areas
        const result = this.calculationEngine.calculateAll(
            areas,
            (areaData) => this.calculateArea(areaData)
        );

        if (result.hasErrors) {
            this.showErrors(result, areas);
            return;
        }

        this.calculateAndDisplay(result);
    }

    /**
     * Calculate and display results
     *
     * @param {Object} data - Calculation data
     */
    calculateAndDisplay(data) {
        // Always use safety buffer (20%) for all cases
        // This accounts for settling, compression, and ensures enough material
        const safetyBuffer = this.productManager?.calculatorProducts?.safety_buffer || 20;

        // Get product sizes from calculator products
        const productSizes = this.extractProductSizes();

        // Calculate products with packaging
        const products = this.calculationEngine.calculateProducts(
            data,
            safetyBuffer,
            false, // don't round to 100 - show exact volume, but packaging rounds to full bags
            productSizes,
            this.productManager?.calculatorProducts?.available_bag_sizes
        );

        // Reset product manager lock state
        if (this.productManager) {
            this.productManager.resetLockState();
        }

        // Display results
        const resultsFragment = this.resultsDisplay.displayResults(
            data,
            products,
            this.calculationEngine.formatDimensionsText.bind(this.calculationEngine)
        );

        if (!resultsFragment) return;

        // Render products
        const substrateContainer = resultsFragment.querySelector('[data-substrate-calculator="substrate-products"]');
        const drainageContainer = resultsFragment.querySelector('[data-substrate-calculator="drainage-products"]');

        if (substrateContainer && this.productManager) {
            this.productManager.renderProductItems(substrateContainer, products.substrate, 'substrate');
        }

        if (drainageContainer && this.productManager) {
            this.productManager.renderProductItems(drainageContainer, products.drainage, 'drainage');
        }

        // Update checkbox labels
        this.updateCheckboxLabels(resultsFragment, products);

        // Render related products (cross-sells)
        if (this.productManager) {
            this.productManager.renderRelatedProducts(resultsFragment);
        }

        // Add event listeners
        if (this.productManager) {
            this.productManager.setupProductListeners(resultsFragment);
        }

        // Hide areas and calculate button
        const areaContainer = this.root.querySelector('[data-substrate-calculator="area-container"]');
        const calculateBtn = this.root.querySelector('[data-substrate-calculator="calculate"]');

        if (areaContainer) {
            areaContainer.style.display = 'none';
        }

        if (calculateBtn) {
            calculateBtn.style.display = 'none';
        }

        // Remove old results if they exist (from previous edit)
        const oldResults = this.root.querySelector('[data-substrate-calculator="results-container"]');
        if (oldResults) {
            oldResults.remove();
        }

        // Append results to DOM
        this.root.appendChild(resultsFragment);

        // Calculate initial total price (call without fragment to query from DOM)
        if (this.productManager) {
            this.productManager.updateTotalPrice();
        }

        // Scroll to results (on mobile, scroll to top of results)
        const resultsContainer = this.root.querySelector('[data-substrate-calculator="results-container"]');
        if (resultsContainer) {
            resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    /**
     * Calculate individual area
     *
     * @param {Object} areaData - Area data
     * @returns {Object} Calculation result
     */
    calculateArea(areaData) {
        // Read current values directly from DOM
        const methodRadio = areaData.element.querySelector('input[name^="calculation_method"]:checked');
        const calculationMethod = methodRadio?.value || 'measure';

        const shapeType = areaData.element.dataset.shapeType || 'box';

        const drainageRadio = areaData.element.querySelector('input[name^="drainage"]:checked');
        const withDrainage = drainageRadio?.value === 'with-drainage';

        // Get dimensions from area
        const actualDimensions = this.areaManager.getDimensions(
            areaData.element,
            shapeType,
            calculationMethod
        );

        // Validate dimensions
        const validation = this.validateDimensions(actualDimensions, shapeType, calculationMethod, withDrainage);

        if (!validation.isValid) {
            return {
                valid: false,
                errors: validation.errors
            };
        }

        // Get appropriate calculator
        let calculator;

        // Get settings from ProductManager
        const safetyBuffer = this.productManager?.calculatorProducts?.safety_buffer || 20;
        const drainageRatio = this.productManager?.calculatorProducts?.drainage_ratio || 15;

        // IMPORTANT: Check calculationMethod FIRST, before shapeType
        // When user enters known volume, we must use CustomCalculator regardless of shape type
        if (calculationMethod === 'known' || shapeType === 'custom') {
            // Known volume (liters or m³ - both converted to liters in getDimensions)
            calculator = new CustomCalculator(actualDimensions, withDrainage);
        } else if (shapeType === 'pot') {
            actualDimensions.drainageRatio = drainageRatio;
            calculator = new PotCalculator(actualDimensions, withDrainage);
        } else if (shapeType === 'ground') {
            actualDimensions.drainageRatio = drainageRatio;
            calculator = new GroundCalculator(actualDimensions, withDrainage);
        } else {
            // Box (default) - safety buffer is applied in CalculationEngine
            calculator = new BoxCalculator(actualDimensions, withDrainage);
        }

        return {
            valid: true,
            substrateVolume: calculator.calculateSubstrateVolume(),
            drainageVolume: calculator.calculateDrainageVolume(),
            dimensions: actualDimensions,
            shapeType: shapeType,
            calculationMethod: calculationMethod
        };
    }

    /**
     * Validate dimensions
     *
     * @param {Object} dimensions - Dimensions object
     * @param {string} shapeType - Shape type
     * @param {string} calculationMethod - Calculation method
     * @param {boolean} withDrainage - Whether drainage is enabled
     * @returns {Object} Validation result with errors array containing field names and error details
     */
    validateDimensions(dimensions, shapeType, calculationMethod, withDrainage = false) {
        const errors = [];

        // Maximum reasonable values (to prevent "cosmic" inputs)
        const MAX_VOLUME = 1000000; // 1,000,000 liters (1000 m³)
        const MAX_DIMENSION = 10000; // 10,000 cm (100 meters)
        const MAX_DIAMETER = 5000; // 5,000 cm (50 meters) for pot diameter
        const MAX_AREA = 10000; // 10,000 m² (1 hectare)

        // Minimum depth for box with drainage (drainage thickness = 5cm)
        const MIN_BOX_DEPTH_WITH_DRAINAGE = 5.1; // 5.1 cm to allow some substrate above drainage

        if (calculationMethod === 'known' || shapeType === 'custom') {
            // Validate volume (either from liters or m³)
            if (!dimensions.volume || dimensions.volume <= 0) {
                errors.push({ field: 'volume', type: 'required' });
            } else if (dimensions.volume > MAX_VOLUME) {
                errors.push({ field: 'volume', type: 'too_large', max: MAX_VOLUME, unit: 'liters' });
            }
        } else if (shapeType === 'pot') {
            if (!dimensions.d || dimensions.d <= 0) {
                errors.push({ field: 'd', type: 'required' });
            } else if (dimensions.d > MAX_DIAMETER) {
                errors.push({ field: 'd', type: 'too_large', max: MAX_DIAMETER, unit: 'centimeters' });
            }
            if (!dimensions.h || dimensions.h <= 0) {
                errors.push({ field: 'h', type: 'required' });
            } else if (dimensions.h > MAX_DIMENSION) {
                errors.push({ field: 'h', type: 'too_large', max: MAX_DIMENSION, unit: 'centimeters' });
            }
        } else if (shapeType === 'ground') {
            if (!dimensions.area || dimensions.area <= 0) {
                errors.push({ field: 'area', type: 'required' });
            } else if (dimensions.area > MAX_AREA) {
                errors.push({ field: 'area', type: 'too_large', max: MAX_AREA, unit: 'square meters' });
            }
            if (!dimensions.depth || dimensions.depth <= 0) {
                errors.push({ field: 'depth', type: 'required' });
            } else if (dimensions.depth > MAX_DIMENSION) {
                errors.push({ field: 'depth', type: 'too_large', max: MAX_DIMENSION, unit: 'centimeters' });
            }
        } else {
            // box (default)
            if (!dimensions.x || dimensions.x <= 0) {
                errors.push({ field: 'x', type: 'required' });
            } else if (dimensions.x > MAX_DIMENSION) {
                errors.push({ field: 'x', type: 'too_large', max: MAX_DIMENSION, unit: 'centimeters' });
            }
            if (!dimensions.y || dimensions.y <= 0) {
                errors.push({ field: 'y', type: 'required' });
            } else if (dimensions.y > MAX_DIMENSION) {
                errors.push({ field: 'y', type: 'too_large', max: MAX_DIMENSION, unit: 'centimeters' });
            }

            // Special validation for depth (z) with drainage
            if (!dimensions.z || dimensions.z <= 0) {
                errors.push({ field: 'z', type: 'required' });
            } else if (dimensions.z > MAX_DIMENSION) {
                errors.push({ field: 'z', type: 'too_large', max: MAX_DIMENSION, unit: 'centimeters' });
            } else if (withDrainage && dimensions.z <= 5) {
                // When drainage is enabled, depth must be more than 5cm
                errors.push({ field: 'z', type: 'too_small', min: MIN_BOX_DEPTH_WITH_DRAINAGE, unit: 'centimeters' });
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Extract product sizes from calculator products
     *
     * @returns {Object} Product sizes for each type
     */
    extractProductSizes() {
        const products = this.productManager?.calculatorProducts || {};

        // Helper to get best product from array (prefer in stock products)
        const getBestProduct = (productArray, fallbackSize) => {
            if (!productArray || !Array.isArray(productArray) || productArray.length === 0) {
                return { size: fallbackSize, product: null };
            }

            // First try to find in-stock products
            const inStockProducts = productArray.filter(p => p.is_in_stock);

            if (inStockProducts.length > 0) {
                // Return first in-stock product
                return { size: inStockProducts[0]?.unit_price_factor || fallbackSize, product: inStockProducts[0] };
            }

            // Fallback to first product even if out of stock
            return { size: productArray[0]?.unit_price_factor || fallbackSize, product: productArray[0] };
        };

        const substrateBag = getBestProduct(products.substrate_bag, 50);
        const substrateMediumBag = getBestProduct(products.substrate_medium_bag, 20);
        const substrateSmallBag = getBestProduct(products.substrate_small_bag, 5);
        const substratePallet = getBestProduct(products.substrate_pallet, 1200);
        const drainageBag = getBestProduct(products.drainage_bag, 50);
        const drainageSmallBag = getBestProduct(products.drainage_small_bag, 4);
        const drainageBigBag = getBestProduct(products.drainage_pallet, 2000);

        const productSizes = {
            substrateBagSize: substrateBag.size,
            substrateMediumBagSize: substrateMediumBag.size,
            substrateSmallBagSize: substrateSmallBag.size,
            substratePalletSize: substratePallet.size,
            drainageBagSize: drainageBag.size,
            drainageBigBagSize: drainageBigBag.size,
            // Store product references for availability checks
            products: {
                substrateBag: substrateBag.product,
                substrateMediumBag: substrateMediumBag.product,
                substrateSmallBag: substrateSmallBag.product,
                substratePallet: substratePallet.product,
                drainageBag: drainageBag.product,
                drainageBigBag: drainageBigBag.product,
            }
        };

        // Only include small bag size if small bag products are actually available
        if (drainageSmallBag.product && products.drainage_small_bag && products.drainage_small_bag.length > 0) {
            productSizes.drainageSmallBagSize = drainageSmallBag.size;
            productSizes.products.drainageSmallBag = drainageSmallBag.product;
        }

        // Clear medium bag size if medium bag products are not available
        if (!substrateMediumBag.product || !products.substrate_medium_bag || products.substrate_medium_bag.length === 0) {
            productSizes.substrateMediumBagSize = null;
        }

        // Clear small bag size if small bag products are not available
        if (!substrateSmallBag.product || !products.substrate_small_bag || products.substrate_small_bag.length === 0) {
            productSizes.substrateSmallBagSize = null;
        }

        // IMPORTANT: Clear pallet size if no pallet product is available
        // This prevents suggesting pallets that don't exist in the product's related items
        if (!substratePallet.product || !products.substrate_pallet || products.substrate_pallet.length === 0) {
            productSizes.substratePalletSize = null;
            delete productSizes.products.substratePallet;
        }

        // IMPORTANT: Use dynamic substrate sizes if available
        // This allows us to use ANY available bag size (50L, 20L, 15L, 10L, 5L, etc.)
        // instead of being limited to the hardcoded 50L, 20L, 5L
        if (products.dynamic_substrate_sizes && Array.isArray(products.dynamic_substrate_sizes)) {
            productSizes.dynamicSubstrateSizes = products.dynamic_substrate_sizes;
        }

        return productSizes;
    }

    /**
     * Update checkbox labels with volume breakdown
     *
     * @param {DocumentFragment} fragment - Results fragment
     * @param {Object} products - Products data
     */
    updateCheckboxLabels(fragment, products) {
        // Get product sizes from products object
        const productSizes = products.productSizes || this.extractProductSizes();

        // Update substrate label
        const substrateLabel = fragment.querySelector('[data-substrate-calculator="substrate-label"]');
        if (substrateLabel && products.substrate.totalVolume > 0) {
            const substrateProductSizes = {
                bagSize: productSizes.substrateBagSize,
                palletSize: productSizes.substratePalletSize
            };
            // Only include medium bag size if it exists
            if (productSizes.substrateMediumBagSize) {
                substrateProductSizes.mediumBagSize = productSizes.substrateMediumBagSize;
            }
            // Only include small bag size if it exists
            if (productSizes.substrateSmallBagSize) {
                substrateProductSizes.smallBagSize = productSizes.substrateSmallBagSize;
            }
            const breakdownText = this.calculationEngine.formatPackagingBreakdown(
                products.substrate,
                'ziemi',
                'substrate',
                substrateProductSizes
            );
            substrateLabel.innerHTML = breakdownText;
        }

        // Update drainage label
        const drainageLabel = fragment.querySelector('[data-substrate-calculator="drainage-label"]');
        if (drainageLabel && products.drainage.totalVolume > 0) {
            const breakdownText = this.calculationEngine.formatPackagingBreakdown(
                products.drainage,
                'keramzytu',
                'drainage',
                {
                    bagSize: productSizes.drainageBagSize,
                    smallBagSize: productSizes.drainageSmallBagSize,
                    bigBagSize: productSizes.drainageBigBagSize
                }
            );
            drainageLabel.innerHTML = breakdownText;
        }
    }

    /**
     * Show errors on areas
     *
     * @param {Object} result - Calculation result
     * @param {Array} areas - Areas array
     */
    showErrors(result, areas) {
        let firstErrorArea = null;

        result.areas.forEach(areaResult => {
            if (!areaResult.valid) {
                const areaData = areas.find(a => a.id === areaResult.areaId);
                if (areaData) {
                    this.showAreaErrors(areaData.element, areaResult.errors);
                    if (!firstErrorArea) {
                        firstErrorArea = areaData.element;
                    }
                }
            }
        });

        this.showError(t('fix_errors'));

        // Scroll to first area with errors
        if (firstErrorArea) {
            firstErrorArea.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }

    /**
     * Show errors on specific area
     *
     * @param {HTMLElement} area - Area element
     * @param {Array} errors - Array of error objects with field, type, and details
     */
    showAreaErrors(area, errors) {
        // Clear errors from this area first
        this.clearAreaErrors(area);

        // Map field names to data-substrate-calculator selectors
        const fieldToSelector = {
            'x': '[data-substrate-calculator="dimension-width"]',
            'y': '[data-substrate-calculator="dimension-length"]',
            'z': '[data-substrate-calculator="dimension-depth"]',
            'd': '[data-substrate-calculator="dimension-depth"]',           // Pot diameter (uses depth input)
            'h': '[data-substrate-calculator="dimension-length"]',          // Pot height (uses length input)
            'volume': '[data-substrate-calculator="known-volume"], [data-substrate-calculator="known-cubic-meters"]',
            'area': '[data-substrate-calculator="dimension-depth"]',        // Ground area (uses depth input)
            'depth': '[data-substrate-calculator="dimension-length"]'       // Ground depth (uses length input)
        };

        errors.forEach(error => {
            // Support both old string format and new object format
            const errorField = typeof error === 'string' ? error : error.field;
            const errorType = typeof error === 'string' ? 'required' : error.type;
            const errorDetails = typeof error === 'string' ? {} : error;

            const selector = fieldToSelector[errorField];
            if (selector) {
                const inputs = area.querySelectorAll(selector);
                inputs.forEach(input => {
                    // Only highlight visible input
                    if (input.offsetParent !== null) {
                        input.classList.add('border-red-500');

                        // Add error message element
                        let errorMsg = input.parentElement.querySelector('[data-substrate-calculator-error]');
                        if (!errorMsg) {
                            errorMsg = document.createElement('span');
                            errorMsg.setAttribute('data-substrate-calculator-error', 'true');
                            errorMsg.className = 'text-red-600 text-xs mt-1 block';
                            input.parentElement.appendChild(errorMsg);
                        }

                        // Set error message text
                        if (errorType === 'required') {
                            errorMsg.textContent = t('required_field');
                        } else if (errorType === 'too_large' && errorDetails.max) {
                            const unitKey = errorDetails.unit === 'centimeters' ? 'centimeters' :
                                          errorDetails.unit === 'liters' ? 'liters' :
                                          errorDetails.unit === 'square meters' ? 'square_meters' : errorDetails.unit;
                            const unit = t(unitKey) || errorDetails.unit;
                            const maxValue = errorDetails.max.toLocaleString('pl-PL');
                            errorMsg.textContent = t('value_too_large')
                                .replace('%s', `${maxValue} ${unit}`);
                        } else if (errorType === 'too_small' && errorField === 'z') {
                            // Special message for box depth with drainage
                            errorMsg.textContent = t('depth_with_drainage_min');
                        } else if (errorType === 'too_small' && errorDetails.min) {
                            const unitKey = errorDetails.unit === 'centimeters' ? 'centimeters' :
                                          errorDetails.unit === 'liters' ? 'liters' :
                                          errorDetails.unit === 'square meters' ? 'square_meters' : errorDetails.unit;
                            const unit = t(unitKey) || errorDetails.unit;
                            const minValue = errorDetails.min.toLocaleString('pl-PL');
                            errorMsg.textContent = t('value_too_small')
                                .replace('%s', `${minValue} ${unit}`);
                        } else {
                            errorMsg.textContent = t('invalid_value');
                        }
                    }
                });
            }
        });
    }

    /**
     * Clear errors from area
     *
     * @param {HTMLElement} area - Area element
     */
    clearAreaErrors(area) {
        const inputs = area.querySelectorAll('input');
        inputs.forEach(input => {
            input.classList.remove('border-red-500');
        });

        // Remove error message elements
        const errorMessages = area.querySelectorAll('[data-substrate-calculator-error]');
        errorMessages.forEach(msg => msg.remove());
    }

    /**
     * Show error message
     *
     * @param {string} message - Error message
     */
    showError(message) {
        // Remove existing error messages
        this.clearErrors();

        // Create error message element
        const errorContainer = document.createElement('div');
        errorContainer.className = 'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4';
        errorContainer.setAttribute('data-substrate-calculator-error', 'true');

        const errorText = document.createElement('p');
        errorText.className = 'text-sm font-medium';
        errorText.textContent = message;

        errorContainer.appendChild(errorText);

        // Insert error message before buttons container
        const buttonsContainer = this.root.querySelector('[data-substrate-calculator="buttons-container"]');
        if (buttonsContainer) {
            buttonsContainer.parentNode.insertBefore(errorContainer, buttonsContainer);
        }

        // Scroll error into view
        errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /**
     * Clear error messages
     */
    clearErrors() {
        const errorMessages = this.root.querySelectorAll('[data-substrate-calculator-error]');
        errorMessages.forEach(el => el.remove());
    }

    /**
     * Get fallback products when API fails
     *
     * @returns {Object} Fallback product data
     */
    getFallbackProducts() {
        return {
            substrate_bag: [],
            substrate_pallet: [],
            drainage_bag: [],
            drainage_pallet: [],
            safety_buffer: 20,
            drainage_ratio: 15,
            promotion: { enabled: false, percentage: 0 }
        };
    }
}
